import { Row } from 'react-bootstrap';
import { PropertyBusinessType } from 'models/Properties';
import { FormProvider } from 'components/forms/form/Form';
import PropertyStatusBadge from 'components/domain/property/status/PropertyStatusBadge';
import useAppUser from 'hooks/useAppUser';
import PropertyPicture from 'components/domain/property/picture/PropertyPicture';
import { PropertyForPropertyListItem } from '../Properties.types';
import {
  PropertyListItemStyled,
  PropertyActionsColumnStyled,
  PropertyDetailsColumnStyled,
  PropertyPermissionsColumnStyled,
} from './PropertyListItem.styles';
import PropertyInfo from './PropertyInfo';
import PropertyActions from './PropertyActions';
import PropertyCollapsibleArea from './PropertyCollapsibleArea';
import { useChildProperties, getDefaultFormValues } from './PropertyItem.utils';
import PropertyItemPermissions from './permissions/PropertyItemPermissions';
import useManagePropertyPermissionsAllowed from './permissions/useManagePropertyPermissionsAllowed';

interface PropertyListItemProps {
  parentProperty?: PropertyForPropertyListItem;
  property: PropertyForPropertyListItem;
  updateTagsCallback?: (updatedTags: string[]) => void;
}

const PropertyListItem = ({
  parentProperty,
  property,
  updateTagsCallback,
}: PropertyListItemProps) => {
  const { agency } = useAppUser();
  const childProperties = useChildProperties(property);
  const isPropertyWithChildren = childProperties.length > 0;
  const canManagePermissions = useManagePropertyPermissionsAllowed();
  const isHotel = property.businessType === PropertyBusinessType.HOTEL;
  const isSubUnit = property.businessType === PropertyBusinessType.SUB_UNIT;
  const isChildProperty = !!parentProperty || undefined;
  const canSeePermissions =
    // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    canManagePermissions && agency.usePermissionedPMS && !isHotel && !isSubUnit;

  return (
    <FormProvider defaultValues={getDefaultFormValues(property)}>
      <PropertyListItemStyled
        data-is-child={isChildProperty}
        data-property-uid={property.uid}
      >
        <Row>
          <PropertyDetailsColumnStyled
            $canSeePermissions={canSeePermissions}
            $isChildProperty={isChildProperty}
          >
            <PropertyPicture property={property} />
            <PropertyInfo
              property={property}
              updateTagsCallback={updateTagsCallback}
            />
          </PropertyDetailsColumnStyled>
          <PropertyPermissionsColumnStyled
            $canSeePermissions={canSeePermissions}
          >
            {canSeePermissions && (
              <PropertyItemPermissions property={property} />
            )}
          </PropertyPermissionsColumnStyled>
          <PropertyActionsColumnStyled $canSeePermissions={canSeePermissions}>
            <PropertyStatusBadge isActive={property.isActive} />
            <PropertyActions
              parentProperty={parentProperty}
              property={property}
            />
          </PropertyActionsColumnStyled>
        </Row>

        {isPropertyWithChildren && (
          <PropertyCollapsibleArea property={property}>
            {childProperties.map((childProperty) => (
              <PropertyListItem
                key={childProperty.uid}
                parentProperty={property}
                property={childProperty}
              />
            ))}
          </PropertyCollapsibleArea>
        )}
      </PropertyListItemStyled>
    </FormProvider>
  );
};

PropertyListItem.defaultProps = {
  parentProperty: undefined,
  updateTagsCallback: undefined,
};

export default PropertyListItem;
